/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-account
 * @responsibility Render the SET-F02 Account pane from backend-authoritative profile and session hooks.
 * @state-owner TanStack Query
 * @allowed-imports same-module hooks/components; app session provider; shared responsive/translator model
 * @forbidden direct API client; hardcoded profile data; password UI or copy
 * @public-api AccountSettingsPage
 * @side-effects invokes authorized same-module commands and cache invalidation
 * @tests AccountSettingsSurface.test.tsx
 */

import { useWorkspaceSession } from '../../../app/providers/SessionProvider';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  useProfile,
  useRequestEmailChange,
  useUpdateProfile,
  useUploadAvatar,
} from '../api/settings-account.queries';
import { AccountSettingsSurface } from './AccountSettingsSurface';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function AccountSettingsPage({ mobile }: { mobile: boolean }) {
  const t = useTranslator();
  const { session } = useWorkspaceSession();
  const profileQuery = useProfile();
  const updateProfile = useUpdateProfile();
  const uploadAvatar = useUploadAvatar();
  const requestEmailChange = useRequestEmailChange();

  if (profileQuery.isPending) {
    return (
      <VispoLoading className="account-settings-pane__loading" label={t('正在載入帳號設定')} />
    );
  }

  if (!profileQuery.data) {
    return (
      <div className="account-settings-pane__error" role="alert">
        <span>{t('更新失敗，請重試')}</span>
        <button type="button" onClick={() => void profileQuery.refetch()}>
          {t('重新整理')}
        </button>
      </div>
    );
  }

  const profile = profileQuery.data;

  return (
    <AccountSettingsSurface
      avatarUploadErrorCode={errorCodeOf(uploadAvatar.error)}
      avatarUrl={profile.avatar_url}
      email={session?.email ?? ''}
      emailChangeError={requestEmailChange.isError ? 'error' : null}
      emailChangePending={requestEmailChange.isSuccess}
      emailChangeSending={requestEmailChange.isPending}
      errorCode={errorCodeOf(updateProfile.error)}
      linkedProviders={session?.linkedProviders ?? []}
      mobile={mobile}
      onClearAvatar={() =>
        updateProfile.mutate({ clearAvatar: true, expectedRevision: profile.revision })
      }
      onRequestEmailChange={(newEmail) => requestEmailChange.mutate(newEmail)}
      onResetAvatarUploadError={() => {
        if (uploadAvatar.error) uploadAvatar.reset();
      }}
      onRetry={() => {
        updateProfile.reset();
        void profileQuery.refetch();
      }}
      onSave={(displayName) =>
        updateProfile.mutate({ displayName, expectedRevision: profile.revision })
      }
      onUploadAvatar={(file) => uploadAvatar.mutate(file)}
      profile={profile}
      saving={updateProfile.isPending}
      uploadingAvatar={uploadAvatar.isPending}
    />
  );
}
